import { FastCheckEnv, Materialized, summon } from "@silversea-ssc/functional-support"
//import { Arbitrary } from "fast-check"
import { Branded, Errors, Type } from "io-ts"
import * as F from "fp-ts/function"
import * as E from "fp-ts/Either"
import { PathReporter } from "io-ts/PathReporter"
import { BrandFor } from "./brand-from-predicate"

type Arbitrary<T> = any // WARNING: this is due to bundle issues with fast-check

type ArbitraryFn = (fastCheck: FastCheckEnv["fastCheck"]) => Arbitrary<string> | undefined
type BrandedS<Brand> = Branded<string, Brand>
type RefinedMaterialized<Brand> = Materialized<string, BrandedS<Brand>> & {
    buildUnsafe: (value: string) => BrandedS<Brand>
}

export const brandString = <Brand>(
    type: Type<BrandedS<Brand>, string>,
    arbitrary: ArbitraryFn = F.constUndefined,
): RefinedMaterialized<Brand> => {
    type T = BrandedS<Brand>
    const _T = summon((F) =>
        F.refined(F.string(), type.is, type.name, {
            IoTsURI: (_x, { withMessage }) => withMessage(_x, (x) => `${JSON.stringify(x)} is NOT a ${type.name}`),
            FastCheckURI: (_x, { fastCheck }) => (arbitrary(fastCheck) ?? _x).map((x: string) => x as T),
        }),
    )

    return Object.assign(_T, {
        buildUnsafe: F.flow(_T.strictType.decode, E.getOrElse(unsafeBuildException(type.name))),
    })
}

export const rebrandMaterialized = <OldBrand, E extends string, N extends string>(
    materialized: Materialized<E, BrandedS<OldBrand>>,
    name: N,
): RefinedMaterialized<BrandFor<N> & OldBrand> => {
    type T = BrandedS<BrandFor<N> & OldBrand>
    const _T = summon((F) =>
        F.refined(materialized(F), (x: BrandedS<OldBrand>): x is T => true, name, {
            IoTsURI: (_x, { withMessage }) => withMessage(_x, (x) => `${JSON.stringify(x)} is NOT a ${name}`),
        }),
    )

    return Object.assign(_T, {
        buildUnsafe: F.flow(_T.strictType.decode, E.getOrElse(unsafeBuildException(name))),
    })
}

const unsafeBuildException =
    <T>(name: string) =>
    (e: Errors): T => {
        throw new Error(`Tried to unsafely build a ${name}: ${PathReporter.report(E.left(e))}`)
    }
